/**
 * Command Center / Home DTOs (Issue #1424)
 *
 * Wire types for the AI-powered business-portal home: morning brief,
 * priority signals, suggested prompts, watchlist pins, and Astor threads.
 *
 * Pins are user-scoped and span entity types from multiple owning services
 * (case, loan, application, partner, contact, consumer), which is why HomePin
 * lives in 24-account-service rather than any single domain service.
 */
import { z } from 'zod';

// ============================================================================
// Pinnable entity types
// ============================================================================

/**
 * Pinnable entity types. `'rate'` is a borrower-portal watchlist entity for
 * rate-watch pins (consumer pins "30y rate" and gets notified when crossed —
 * the rate-threshold notification trigger itself lives in 27-notification-service).
 * Other portals can pin records (case/loan/application/partner/contact/consumer).
 */
export const HomePinEntityTypeSchema = z.enum([
  'case',
  'loan',
  'application',
  'partner',
  'contact',
  'consumer',
  'rate',
]);
export type HomePinEntityType = z.infer<typeof HomePinEntityTypeSchema>;

// ============================================================================
// HomePin
// ============================================================================

export const HomePinSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  userId: z.string().uuid(),
  entityType: HomePinEntityTypeSchema,
  entityId: z.string().uuid(),
  /** Display label captured at pin time so the watchlist row stays useful
   *  even if the entity is renamed/archived later. Re-resolved on click. */
  label: z.string().max(255),
  /**
   * Optional secondary label rendered below `label` ("$2.4M Term Loan",
   * "Application — In Review"). Resolved at fetch time from the
   * underlying entity record so the gateway can show context-rich rows
   * without forcing the client to look up entities one-by-one.
   */
  subLabel: z.string().nullable().default(null),
  /**
   * Optional status pill ("At Risk", "In Review", "Monitoring", "Active").
   * Surfaces the entity's lifecycle state on the watchlist row. Drives
   * the chip color via `statusTone` below.
   */
  statusLabel: z.string().nullable().default(null),
  /**
   * Status pill tone — picks a palette family without leaking specific
   * theme tokens through the wire. The renderer maps `'warning' →
   * theme.palette.warning` etc.
   */
  statusTone: z.enum(['neutral', 'info', 'success', 'warning', 'error']).nullable().default(null),
  /**
   * Pin creation timestamp. Typed as `Date | string` to mirror the wire
   * reality: this gateway has no superjson transformer configured, so
   * `z.date()` values serialise to ISO strings over JSON and arrive at
   * the client as strings. Consumers should pass through `toDate()` in
   * `command-center-utils.ts` if they need a Date object — the tRPC
   * RouterOutput type now matches the runtime shape, so the
   * `as unknown as HomePin[]` cast at call sites is no longer needed.
   * Same pattern as `HomeSignalSchema.createdAt` above.
   */
  createdAt: z.union([z.date(), z.string()]),
});
export type HomePin = z.infer<typeof HomePinSchema>;

export const AddHomePinSchema = z.object({
  entityType: HomePinEntityTypeSchema,
  entityId: z.string().uuid(),
  label: z.string().min(1).max(255),
});
export type AddHomePin = z.infer<typeof AddHomePinSchema>;

export const RemoveHomePinSchema = z.object({
  id: z.string().uuid(),
});
export type RemoveHomePin = z.infer<typeof RemoveHomePinSchema>;

// ============================================================================
// Morning brief (LLM-composed, with templated fallback)
// ============================================================================

export const HomeBriefTickerStatSchema = z.object({
  /** i18n key under home.ticker.* with {{count}} interpolation */
  i18nKey: z.string(),
  count: z.number().int().nonnegative(),
});
export type HomeBriefTickerStat = z.infer<typeof HomeBriefTickerStatSchema>;

export const HomeBriefSchema = z.object({
  /**
   * Headline string. When `isFallback=false` this is LLM-composed text in
   * the user's working language (English in v1) and is rendered as-is. When
   * `isFallback=true` the gateway writes a placeholder string here and the
   * client renders `headlineI18nKey + headlineI18nParams` instead so the
   * fallback respects the user's locale.
   */
  headline: z.string(),
  /**
   * Optional i18n key for the templated fallback path. When set, the client
   * renders `t(headlineI18nKey, headlineI18nParams)` and ignores `headline`.
   */
  headlineI18nKey: z.string().nullable().default(null),
  headlineI18nParams: z
    .record(z.string(), z.union([z.string(), z.number()]))
    .nullable()
    .default(null),
  /** Up to 3 key facts the brief is built from (always present so the templated fallback can use them) */
  ticker: z.array(HomeBriefTickerStatSchema).max(5),
  /**
   * Direct top-line counts mirrored from the briefing context. Exposed
   * separately from `ticker` so the dashboard subtitle can render
   * "{N} active loans · {M} unread messages · {K} at SLA risk" with
   * per-segment formatting (e.g. amber tint when SLA > 0) without
   * having to look up entries in the ticker array by i18n key.
   */
  openLoanCount: z.number().int().nonnegative().default(0),
  unreadMessageCount: z.number().int().nonnegative().default(0),
  slaAtRiskCount: z.number().int().nonnegative().default(0),
  generatedAt: z.date(),
  /** True when the LLM composer was unavailable / cold and we used the templated headline */
  isFallback: z.boolean(),
});
export type HomeBrief = z.infer<typeof HomeBriefSchema>;

// ============================================================================
// Priority signals
// ============================================================================

export const HomeSignalClassificationSchema = z.enum(['urgent', 'sla', 'approval', 'message', 'document', 'partner']);
export type HomeSignalClassification = z.infer<typeof HomeSignalClassificationSchema>;

export const HomeSignalUrgencySchema = z.enum(['high', 'medium', 'low']);
export type HomeSignalUrgency = z.infer<typeof HomeSignalUrgencySchema>;

export const HomeSignalSchema = z.object({
  /** Stable ID — `${sourceModule}:${sourceId}` so dedup across re-fetches works */
  id: z.string(),
  /** Display label (often a case title / partner name / message subject — entity-supplied, not localizable) */
  label: z.string(),
  /**
   * Optional second-line i18n key — the gateway picks the key, the client
   * renders it. Avoids hardcoded English ("SLA breached") in the response.
   * Pair with `sublabelI18nParams` when the key needs interpolation.
   */
  sublabelI18nKey: z.string().nullable().default(null),
  sublabelI18nParams: z.record(z.string(), z.string()).nullable().default(null),
  classification: HomeSignalClassificationSchema,
  urgency: HomeSignalUrgencySchema,
  /** Portal-relative path the user lands on when they click the signal */
  deepLink: z.string(),
  /** Which backend produced this signal — useful for debugging + telemetry */
  sourceModule: z.string(),
  /**
   * When the underlying record was created (the SLA-at-risk case, the
   * unread message, the pending approval, etc.). Drives the per-row
   * "NEW" tag + the header's "{N} new" count chip on the home page.
   * Optional because legacy gateways may not populate it; the renderer
   * gracefully omits the freshness affordances when missing.
   */
  createdAt: z.union([z.date(), z.string()]).nullable().default(null),
});
export type HomeSignal = z.infer<typeof HomeSignalSchema>;

// ============================================================================
// Suggested prompts (kind chips per Jam-e Jam parity)
// ============================================================================

export const HomeSuggestedPromptKindSchema = z.enum(['ask', 'find', 'command']);
export type HomeSuggestedPromptKind = z.infer<typeof HomeSuggestedPromptKindSchema>;

export const HomeSuggestedPromptSchema = z.object({
  text: z.string().min(1).max(255),
  kind: HomeSuggestedPromptKindSchema,
});
export type HomeSuggestedPrompt = z.infer<typeof HomeSuggestedPromptSchema>;

// ============================================================================
// Threads (recent Astor conversations)
// ============================================================================

export const HomeThreadSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  lastMessageAt: z.date(),
  /** Portal-relative deep link, typically `/astor?conversation=<id>` */
  deepLink: z.string(),
});
export type HomeThread = z.infer<typeof HomeThreadSchema>;

// ============================================================================
// Brief composer context (passed from gateway → agent-service)
// ============================================================================

export const HomeBriefContextSchema = z.object({
  userId: z.string().uuid(),
  businessId: z.string().uuid(),
  userName: z.string().nullable().optional(),
  businessName: z.string().nullable().optional(),
  openLoanCount: z.number().int().nonnegative(),
  unreadMessageCount: z.number().int().nonnegative(),
  slaAtRiskCount: z.number().int().nonnegative(),
  /** Lightweight strings the LLM can summarise — not full records */
  recentActivity: z.array(z.string()).max(20),
  /** When the user was last on the home page (for "since you left" framing) */
  lastSeenAt: z.date().nullable().optional(),
});
export type HomeBriefContext = z.infer<typeof HomeBriefContextSchema>;

// ============================================================================
// AI Briefing — role-aware "pay attention right now" panel for the home rail
// ============================================================================

export const HomeBriefingSeveritySchema = z.enum(['critical', 'warning', 'info', 'positive']);
export type HomeBriefingSeverity = z.infer<typeof HomeBriefingSeveritySchema>;

/**
 * Map a UserRole-ish string (loan_officer, broker, …) to the i18n key
 * that frames the role context label ("Loan Officer", "Underwriter").
 * Single source of truth — both the gateway and the portal import this
 * so a new role only needs adding once. Returns `null` for unrecognised
 * roles so the surface degrades to a role-neutral view rather than
 * leaking a raw enum value.
 *
 * The labels themselves live under `home.briefing.role.*` in the i18n
 * catalogue — this helper just owns the string→key mapping.
 */
export function getRoleI18nKey(role: string | null | undefined): string | null {
  if (!role) return null;
  switch (role.toLowerCase()) {
    case 'loan_officer':
      return 'home.briefing.role.loanOfficer';
    case 'underwriter':
      return 'home.briefing.role.underwriter';
    case 'processor':
      return 'home.briefing.role.processor';
    case 'broker':
      return 'home.briefing.role.broker';
    case 'principal':
      return 'home.briefing.role.principal';
    case 'ops_manager':
      return 'home.briefing.role.opsManager';
    case 'fund_manager':
      return 'home.briefing.role.fundManager';
    case 'portfolio_manager':
      return 'home.briefing.role.portfolioManager';
    case 'business_admin':
      return 'home.briefing.role.businessAdmin';
    case 'business_user':
      return 'home.briefing.role.businessUser';
    default:
      return null;
  }
}

/**
 * One specific item the user should look at right now. The gateway selects
 * up to 5 items deterministically from the ranked signal stream
 * (`signalsToBriefingItems` slices the top-N) — the LLM does NOT pick or
 * re-order items. The LLM only composes the surrounding `narrative` on
 * `HomeBriefing`; the items themselves are a pure echo of the signal
 * stream the home page already ranks.
 *
 * String-shape contract:
 *   - `heading` is an entity-supplied display string (case title, partner
 *     name, message subject) and is NOT localized — same convention as
 *     `HomeSignal.label`.
 *   - `detail` is an i18n key (always). The gateway sets it to the
 *     signal's `sublabelI18nKey` when one exists (e.g.
 *     `home.signals.sublabel.slaAtRisk`) and falls back to the
 *     classification key (`home.signals.classification.sla`,
 *     `home.signals.classification.urgent`, …) when no sublabel key is
 *     set. Renderers should call `t(detail)` directly — the value
 *     will resolve.
 *
 * The framing (narrative around the items) lives on `HomeBriefing.narrative`.
 */
export const HomeBriefingItemSchema = z.object({
  /** Stable id for keys + click telemetry — `${sourceModule}:${sourceId}` */
  id: z.string(),
  heading: z.string(),
  detail: z.string(),
  /**
   * Interpolation params for `detail` when it's an i18n key that needs them
   * (e.g. `home.signals.sublabel.messageChannel` → `{ channel: 'EMAIL' }`).
   * Renderers must thread this through `t(detail, detailI18nParams)`.
   */
  detailI18nParams: z.record(z.string(), z.string()).nullable().default(null),
  severity: HomeBriefingSeveritySchema,
  /** Portal-relative deep link, optional — some items are pure observations */
  deepLink: z.string().nullable().default(null),
});
export type HomeBriefingItem = z.infer<typeof HomeBriefingItemSchema>;

export const HomeBriefingSchema = z.object({
  /**
   * 1-3 sentence narrative framing what the user should focus on right now,
   * tailored to their role (loan_officer, underwriter, broker, owner, …).
   * LLM-composed when possible; falls back to a templated headline when the
   * model is unavailable. Always plain text — render with `<Typography>`.
   */
  narrative: z.string(),
  /**
   * Optional i18n key for the templated fallback path. When set, the client
   * renders `t(narrativeI18nKey, narrativeI18nParams)` and ignores `narrative`.
   * Same convention as HomeBriefSchema so locale falls through gracefully.
   */
  narrativeI18nKey: z.string().nullable().default(null),
  narrativeI18nParams: z
    .record(z.string(), z.union([z.string(), z.number()]))
    .nullable()
    .default(null),
  /** Up to 5 specific items the user should look at — picked by severity */
  items: z.array(HomeBriefingItemSchema).max(5),
  /** Role the briefing was framed for (echoed for client display + telemetry) */
  role: z.string().nullable().default(null),
  /**
   * i18n key for the role label ("…as a Loan Officer"). Picked by the
   * gateway so an unrecognised role string degrades to `null` rather than
   * surfacing a raw enum value in the UI. Pair with `t(roleI18nKey)` to
   * render — no params needed.
   */
  roleI18nKey: z.string().nullable().default(null),
  generatedAt: z.date(),
  isFallback: z.boolean(),
});
export type HomeBriefing = z.infer<typeof HomeBriefingSchema>;

// ============================================================================
// AI Insights — last-7-days retro + next-7-days forecast
// ============================================================================

/**
 * One stat in a 7-day insights section ("12 loans closed", "3 SLA breaches").
 * Label comes back as an i18n key so the gateway never ships English. The
 * count rides as the only interpolation param (`{{count}}`), which keeps the
 * key shape simple and matches the existing `home.ticker.*` convention.
 */
export const HomeInsights7dStatSchema = z.object({
  i18nKey: z.string(),
  count: z.number().int().nonnegative(),
});
export type HomeInsights7dStat = z.infer<typeof HomeInsights7dStatSchema>;

export const HomeInsights7dSectionSchema = z.object({
  /** 1-2 sentence narrative — LLM-composed reflection / forecast */
  narrative: z.string(),
  /**
   * Templated fallback (same pattern as HomeBriefingSchema). When set, the
   * client renders the i18n key and ignores `narrative`.
   */
  narrativeI18nKey: z.string().nullable().default(null),
  narrativeI18nParams: z
    .record(z.string(), z.union([z.string(), z.number()]))
    .nullable()
    .default(null),
  /** Up to 4 ticker stats (loans closed, applications received, SLAs at risk, …) */
  stats: z.array(HomeInsights7dStatSchema).max(4),
});
export type HomeInsights7dSection = z.infer<typeof HomeInsights7dSectionSchema>;

export const HomeInsights7dSchema = z.object({
  lastSevenDays: HomeInsights7dSectionSchema,
  nextSevenDays: HomeInsights7dSectionSchema,
  generatedAt: z.date(),
  isFallback: z.boolean(),
});
export type HomeInsights7d = z.infer<typeof HomeInsights7dSchema>;
